import { browser } from '$app/env';
import { localStorageSupported } from '#lib/helper/browser.js';
import { detectLocale, type AppLocale, type TranslationVars } from '#lib/helper/locale.js';
import { derived, get, writable } from 'svelte/store';

export const selectedLocale = writable<AppLocale>(detectLocale());

type Translations = {
	[key in AppLocale]?: {
		[key: string]: string | undefined;
	};
};

// Load the json file that contains all the translations.
// format: { "homepage.title": "Hello, World!" }
const translations = writable<Translations>({});

export async function loadTranslations(locale: AppLocale, isWidget = false) {
	try {
		const module = await import(`#lib/locale/${isWidget ? 'widget.' : ''}${locale}.js`);
		let newTranslations = { ...module.default };

		const promises: Promise<void>[] = [];

		if (!isWidget) {
			promises.push(
				import(`#lib/locale/email.${locale}.js`)
					.then((emailModule) => {
						newTranslations = {
							...newTranslations,
							...emailModule.default
						};
					})
					.catch((error) => {
						console.error(`Error loading email translations for ${locale}:`, error);
					})
			);
		}

		if (!browser) {
			// Load the widget translations if we are on server side (load both app and widget translations)
			promises.push(
				import(`#lib/locale/widget.${locale}.js`)
					.then((widgetModule) => {
						newTranslations = {
							...newTranslations,
							...widgetModule.default
						};
					})
					.catch((error) => {
						console.error(`Error loading widget translations for ${locale}:`, error);
					})
			);
		}

		if (promises.length > 0) {
			await Promise.all(promises);
		}

		translations.update((t) => {
			t[locale] = newTranslations;
			return t;
		});
	} catch (error) {
		console.error(`Error loading translations for ${locale}:`, error);
	}
}

let currentLocale: AppLocale | null = null;
let currentIsWidget: boolean | null = null;

function checkAndLoad() {
	const locale = get(selectedLocale);

	let isWidget = false;
	if (browser) {
		isWidget = window.location.pathname.startsWith('/widget');
	}

	if (locale !== currentLocale || isWidget !== currentIsWidget) {
		currentLocale = locale;
		currentIsWidget = isWidget;
		loadTranslations(locale, isWidget);
	}
}

selectedLocale.subscribe((locale) => {
	if (browser && localStorageSupported()) {
		localStorage.setItem('locale', locale);
	}

	checkAndLoad();
});

if (browser) {
	checkAndLoad();
}

const reportedMissingKeys = new Set<string>();

export function clearReportedMissingKeys(): void {
	reportedMissingKeys.clear();
}

// Translation function
function translate(translations: Translations, key: string, vars: TranslationVars = {}) {
	if (!key) return '';

	// Check if translations are loaded, if not, return an empty string.
	const locale = get(selectedLocale);
	const localDict = translations[locale];
	if (!localDict) return '';

	// if key doesn't exist, return the key
	let text = key;
	const val = localDict[key];
	if (val === undefined) {
		const reportKey = `${locale}:${key}`;
		if (!reportedMissingKeys.has(reportKey)) {
			reportedMissingKeys.add(reportKey);
			const msg = `Translation for "${key}" in "${locale}" locale not found`;
			console.error(msg);
		}
	} else {
		text = val;
	}

	// Replace any passed-in variables in the translation string.
	Object.keys(vars).forEach((k) => {
		const regex = new RegExp(`{{${k}}}`, 'g');
		text = text.replace(regex, String(vars[k]));
	});

	return text.replace(/\n/g, '<br />');
}

// Get translations from the current local which match the given matcher function.
export const findTranslations = derived(
	translations,
	($translations) => (matcher: (key: string, value: string) => boolean) => {
		const locale = get(selectedLocale);
		const localTranslations = $translations[locale];
		if (!localTranslations) return [];

		const results = [];
		for (const key in localTranslations) {
			const val = localTranslations[key];
			if (
				Object.prototype.hasOwnProperty.call(localTranslations, key) &&
				val !== undefined &&
				matcher(key, val)
			) {
				results.push({ key, value: val });
			}
		}

		return results;
	}
);

export const t = derived(
	translations,
	($translations) =>
		(key: string, vars: TranslationVars = {}) =>
			translate($translations, key, vars)
);
